<template>
  <ClientOnly>
    <Teleport v-if="isModalOpen" to=".selection-modal-target">
      <div
        class="fixed inset-x-0 top-0 z-60 mx-auto flex max-h-screen w-full max-w-5xl flex-wrap gap-4 overflow-auto bg-white px-4 py-8 sm:mt-20 sm:px-10"
      >
        <button
          class="absolute right-0 top-0 z-50 cursor-pointer p-3"
          :title="$t('dialog.close')"
          :aria-label="$t('dialog.close')"
          @click="closeModal"
        >
          <Icon icon="close" />
        </button>

        <div v-if="products" class="w-1/4 flex-none sm:w-14 lg:w-28">
          <div
            v-for="product in products"
            :key="product.id"
            class="max-h-48 border"
            :style="{ aspectRatio: '3/4' }"
            :class="{
              'border-black': product.id === selectedProductId,
              'border-transparent': product.id !== selectedProductId,
            }"
          >
            <button
              class="relative flex items-end"
              :title="
                $t('outfits.select_product', {
                  productName: getProductName(product),
                })
              "
              :aria-label="
                $t('outfits.select_product', {
                  productName: getProductName(product),
                })
              "
              @click.prevent="handleProductSelection(product.id)"
            >
              <span
                v-if="isInBasket({ productId: product.id }, basket.items.value)"
                class="absolute right-0 top-0 z-10 p-3 text-right"
              >
                <Icon icon="check" />
              </span>
              <ProductImage
                v-if="
                  getHoverImage(product.images) || getFrontImage(product.images)
                "
                fit="contain"
                :image="
                  (getHoverImage(product.images) ||
                    getFrontImage(product.images))!
                "
                :alt="getImageLabel(product)"
                :should-trim="false"
                is-centered
              />
            </button>
          </div>
        </div>

        <div class="relative w-auto grow">
          <ProductImage
            v-if="getImage"
            fit="contain"
            :image="getImage"
            :alt="currentProductLabel"
            sizes="sm:100vw"
            :should-trim="false"
            is-centered
            class="absolute inset-0"
          />
        </div>

        <div v-if="currentProduct" class="md:w-1/2">
          <div class="w-full bg-white">
            <div v-if="currentProduct.isSoldOut" class="left-0 top-0">
              <ProductBadge
                class="ml-0"
                :badge-label="
                  $t(
                    `badge_labels.${getBadgeLabel({
                      isSoldOut: currentProduct.isSoldOut,
                    })}`,
                  )
                "
              />
            </div>

            <div>
              <h1 class="font-semibold leading-tight">
                <span class="text-xl" data-qa="product-description">
                  {{ productName }}
                </span>
              </h1>
              <span class="text-base text-[#6B6B6B]">
                {{ `${$t('pdp.main_order_number')} ${productOrderNumber}` }}
              </span>
              <div class="max-w-xs space-y-4">
                <nuxt-picture
                  v-if="brandLogo"
                  width="100"
                  :modifiers="{ trim: 1 }"
                  :height="100"
                  :src="brandLogo"
                />
              </div>
            </div>

            <ProductPrice
              v-if="price"
              class="mb-1 mt-8"
              :price="price"
              :applied-reductions="price.appliedReductions"
              show-tax-info
              :show-price-reduction-badge="hasSpecial"
              :show-shipping-cost-hint="showShippingCostHint"
              type="whisper"
              is-pdp
            />

            <div class="space-y-6">
              <ProductDetailGroup v-if="showProductSizePicker">
                <ProductSizePicker
                  :product="currentProduct"
                  :variants="productVariants"
                  :selected-variant="activeVariant"
                  @select-size="handleSelectedSize"
                  @pre-select-size="handlePreSelectSize"
                />
              </ProductDetailGroup>
              <div class="space-y-2">
                <div class="flex items-center space-x-2">
                  <Button
                    data-test-id="add-item-to-basket-button"
                    no-padding
                    type="primary"
                    class="grow py-2"
                    :class="{
                      'cursor-not-allowed': !!currentProduct.isSoldOut,
                    }"
                    :disabled="!(canPurchase && canPurchaseMultiple)"
                    :title="!canPurchase ? $t('badge_labels.sold_out') : ''"
                    ignore-capitalisation
                    data-qa="add-to-cart"
                    @click="addItemToBasket"
                  >
                    {{
                      canPurchase
                        ? $t('pdp.add_label')
                        : $t('badge_labels.sold_out')
                    }}
                  </Button>
                </div>
                <div>
                  <NuxtLink
                    class="block py-2 text-center underline"
                    :to="toLocalePath(routeList.basket.path)"
                  >
                    {{ $t('pdp.to_basket') }}
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Backdrop
        v-if="isModalOpen"
        :show="isModalOpen"
        class="z-50"
        @click="closeModal"
      />
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
import type {
  BasketItemDisplayDataItem,
  Product,
  Variant,
} from '@scayle/storefront-nuxt'
import {
  getFirstAttributeValue,
  getAttributeValue,
  getPrice,
  getLowestPrice,
} from '@scayle/storefront-nuxt'
import { ref, computed, effectScope } from 'vue'
import { useI18n } from 'vue-i18n'

import ProductImage from '../product/ProductImage.vue'
import Backdrop from '#layers/base/components/ui/backdrop/Backdrop.vue'
import Icon from '#layers/base/components/ui/icons/Icon.vue'
import ProductDetailGroup from '#layers/base/components/product/productDetail/ProductDetailGroup.vue'
import ProductSizePicker from '#layers/base/components/product/pickers/ProductSizePicker.vue'
import ProductPrice from '#layers/base/components/product/ProductPrice.vue'

import useAgsBasket from '~/composables/useAgsBasket'
import { useBasketDeliveryEstimate } from '#layers/base/composables/useBasketDeliveryEstimate'
import useSmartShopConfiguration from '#layers/base/composables/useSmartShopConfiguration'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import useSizeDisplayData from '#layers/base/composables/useSizeDisplayData'
import useUiState from '~/composables/ui/useUiState'
import { Action } from '#layers/base/plugins/06.toast'

const props = withDefaults(
  defineProps<{
    isModalOpen: boolean
    selectedProduct: number | undefined
    products: Product[] | null
    listName: string
  }>(),
  {
    isModalOpen: false,
    products: () => [],
    listName: '',
  },
)

const emit = defineEmits(['close-modal'])

const { $alert } = useNuxtApp()
const i18n = useI18n()
const { toLocalePath } = useRouteHelpers()
const { getSizeDisplayData } = useSizeDisplayData()
const preSelectedVariant = ref<Variant | null>(null)
const { getBaskedDeliveryEstimationWithDate } = useBasketDeliveryEstimate()
const { trackAddToBasket, trackViewItem, trackSelectItem } = useTrackingEvents()
const scope = effectScope()
const { openAddToBasketFlyout } = useUiState()

const [basket, { data: shopConfiguration }] = await Promise.all([
  useAgsBasket(),
  useSmartShopConfiguration(),
])

const selectedProductId = ref(props.selectedProduct || props.products?.[0]?.id)
const currentProduct = computed(() => {
  return props.products && props.products.length > 0
    ? (props.products.find(
        (product) => product.id === selectedProductId.value,
      ) ?? props.products[0])
    : undefined
})

function setActiveVariantFromBasketState(productId: number): void {
  const variant: Variant | undefined = basket.findItem(
    { productId },
    basket.items.value,
  )?.variant
  activeVariant.value = variant || preSelectedVariant.value || undefined
}

const activeVariant = ref<Variant>()
const productVariants = computed(() => currentProduct.value?.variants || [])
const price = computed(() => {
  return activeVariant.value
    ? getPrice(activeVariant.value)
    : getLowestPrice(productVariants.value)
})

scope.run(() => {
  useWatch(basket.data, () => {
    if (activeVariant.value || !selectedProductId.value) {
      return
    }
    setActiveVariantFromBasketState(selectedProductId.value)
  })
  useWatch(
    () => props.selectedProduct,
    (newVal) => {
      if (newVal) {
        handleProductSelection(newVal as number)
        setActiveVariantFromBasketState(newVal as number)
      }
    },
  )
  useWatch(selectedProductId, () => {
    if (!selectedProductId.value) {
      return
    }
    setActiveVariantFromBasketState(selectedProductId.value)
  })
})

const closeModal = () => emit('close-modal')
const getProductName = (product?: Product) => {
  return getFirstAttributeValue(product?.attributes, 'name')?.label
}
const productName = computed(
  () => getProductName(currentProduct.value) || i18n.t('wishlist.product'),
)
const productOrderNumber = computed(() =>
  getFormattedOrderNumber(currentProduct.value ?? undefined),
)
const deliveryEstimationInDays = computed(() =>
  getDeliveryEstimationInDays(shopConfiguration.value),
)
const isThisAPremiumClubProduct = computed(() =>
  isPremiumClubProduct(currentProduct?.value || null),
)
const isQuantityChangeDisabled = computed(() =>
  getIsQuantityChangeDisabled(currentProduct.value ?? undefined),
)
const canPurchase = computed(() => {
  if (!(currentProduct.value && productVariants.value)) {
    return false
  }
  if (!currentProduct.value.isSoldOut) {
    return true
  }

  for (const item of productVariants.value) {
    if (!item.stock.isSellableWithoutStock) {
      return false
    }
  }
  return true
})

const canPurchaseMultiple = computed(() => {
  if (!currentProduct.value) {
    return false
  }
  if (!isQuantityChangeDisabled.value) {
    return true
  }

  return !basket.contains(
    { productId: currentProduct.value?.id },
    basket.items.value,
  )
})
const handlePreSelectSize = (variant: Variant) => {
  preSelectedVariant.value = variant
}
const handleSelectedSize = (variant: Variant) => {
  activeVariant.value = variant
}

const addItemToBasket = async () => {
  if (activeVariant.value === undefined) {
    // TODO: translation key doesnt fit  maybe; no_item_selected
    $alert.show(
      i18n.t('basket.notification.add_basket'),
      Action.route,
      undefined,
    )
    return
  }

  await basket.addItem({
    variantId: activeVariant.value.id,
    quantity: 1,
    displayData: {
      'attribute-2': getSizeDisplayData(
        activeVariant.value,
      ) as unknown as BasketItemDisplayDataItem,
      ...getBaskedDeliveryEstimationWithDate(
        getDeliveryEstimation(
          activeVariant.value,
          deliveryEstimationInDays.value,
        ),
      ),
    },
    customData: {
      ordernumber: productOrderNumber.value,
      deliveryEstimationDate: getDeliveryEstimation(
        activeVariant.value,
        deliveryEstimationInDays.value,
      ),
    },
  })

  openAddToBasketFlyout(activeVariant.value.id)

  if (currentProduct.value) {
    trackAddToBasket({
      product: currentProduct.value,
      variant: activeVariant.value,
      type: 'outfitpage',
    })
  }
}

const handleProductSelection = (productId: number) => {
  preSelectedVariant.value = null
  selectedProductId.value = productId

  const index =
    props.products?.findIndex((p) => p.id === currentProduct.value?.id) || 0
  if (currentProduct.value) {
    trackSelectItem({
      product: currentProduct.value,
      index,
      listType: 'outfit',
      listName: props.listName,
    })
    trackViewItem({
      product: currentProduct.value,
      variant: activeVariant?.value,
      index,
    })
  }
}

const brandLogo = computed(() =>
  getAttributeValue(currentProduct.value?.attributes, 'brandLogo'),
)
const hasSpecial = computed(
  () => !activeVariant.value && !!price.value?.appliedReductions.length,
)
const showProductSizePicker = computed(() => !isThisAPremiumClubProduct.value)
const showShippingCostHint = computed(() => !isThisAPremiumClubProduct.value)
const isInBasket = computed(() => basket.contains)

const getImageLabel = (product: Product) =>
  getFirstAttributeValue(product?.attributes, 'name')?.label || ''

const getImage = computed(
  () =>
    getHoverImage(currentProduct.value?.images) ||
    getFrontImage(currentProduct.value?.images),
)

const currentProductLabel = computed(
  () =>
    getFirstAttributeValue(currentProduct.value?.attributes, 'name')?.label ||
    '',
)
</script>
